'use client';

import { motion, useReducedMotion } from 'framer-motion';

import type { FormOption, MultiSelectValidation } from '../../types/form-section';
import { isMutuallyExclusiveMultiSelectOption } from '../../lib/unselected-choice-context';
import { testAnchorJoin } from '../../lib/testAnchors';
import { SearchableSelectInput } from './SearchableSelectInput';

export type FieldVariant = 'clustered' | 'spotlight';

/* -------------------------------------------------------------------------
 * Shared atomics — choice buttons + drawn check (used by yes/no + selects)
 * ----------------------------------------------------------------------- */

/**
 * Clustered answer chip. More present than the old hairline pill: white fill,
 * soft navy shadow, ≥44px tap target. Selected = solid teal with a soft teal
 * glow — the fill IS the indicator. Focus ring always brand teal.
 */
// Left-aligned content + text: a long option (e.g. a multi-select choice that
// wraps to two lines) reads cleanly under itself with the glyph at the left,
// instead of centering its wrapped text inside a grown pill.
const CHIP_BASE =
  'inline-flex min-h-[44px] items-center justify-start rounded-full border px-5 py-2 text-left font-body text-[15px] leading-[1.35] transition-[border-color,background-color,color,box-shadow] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2';
// Neutral navy-tinted hairline (the member theme defines no `border-*` token;
// an explicit value renders identically in both the member app and the hub
// preview, and stays on-brand with the navy-based shadow palette).
const CHIP_INACTIVE =
  'border-[rgba(26,35,50,0.16)] bg-bg-card text-text-secondary shadow-[0_1px_2px_rgba(26,35,50,0.05)] hover:border-accent/60 hover:text-text-primary';
const CHIP_ACTIVE =
  'border-accent-dark bg-accent-dark font-medium text-[var(--cream)] shadow-[0_2px_10px_rgba(36,114,114,0.28)]';
// Unselected multi-select option when the max is already reached — dimmed +
// non-actioning (the cue says "Select up to N"; deselect one to swap).
const CHIP_BLOCKED =
  'border-[rgba(26,35,50,0.14)] bg-transparent text-text-tertiary opacity-60 cursor-not-allowed shadow-none';

/**
 * Spotlight answer option (sequential mode). Full-width white card-button —
 * the demo's pattern, refined: one quiet resting shadow, accent-wash fill +
 * inset accent edge when selected (inset shadow thickens the border without
 * a layout shift). Content is LEFT-ALIGNED: an always-present leading control
 * (radio for single-choice, checkbox for multi) sits flush-left and the label
 * reads under itself, so a long or wrapped option doesn't center awkwardly.
 */
const OPTION_BASE =
  'flex w-full min-h-[56px] items-center justify-start gap-3 rounded-[16px] border px-5 py-4 text-left font-body text-[16px] leading-[1.4] transition-[border-color,background-color,color,box-shadow] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2';
const OPTION_INACTIVE =
  'border-[rgba(26,35,50,0.10)] bg-bg-card text-text-primary shadow-[0_1px_3px_rgba(26,35,50,0.07)] hover:border-accent/50 hover:shadow-[0_2px_8px_rgba(26,35,50,0.09)]';
const OPTION_ACTIVE =
  'border-accent bg-accent-light font-medium text-text-primary shadow-[inset_0_0_0_1px_var(--accent)]';
const OPTION_BLOCKED =
  'border-[rgba(26,35,50,0.08)] bg-bg-card text-text-tertiary opacity-60 cursor-not-allowed shadow-none';

/**
 * Small SVG check mark in brand teal. Stroke matches the design system's
 * "feather-style line icon" rule (1.5-2px stroke, round caps).
 */
export function CheckMarkIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`${className} text-accent`}
    >
      <path d="M4 10.5L8 14.5L16 6" />
    </svg>
  );
}

/**
 * Leading radio affordance for single-choice spotlight options (single_select,
 * yes/no). Always present so the left-aligned label sits at a fixed offset with
 * no shift on selection; the inner accent dot scales in when chosen.
 * `aria-hidden` — selection rides on the parent button's role="radio" +
 * aria-checked (downstream code matches options by `button.textContent`, so the
 * glyph must contribute no text).
 */
export function SpotlightRadio({ checked }: { checked: boolean }) {
  const reduceMotion = useReducedMotion() ?? false;
  return (
    <span
      aria-hidden="true"
      className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-[1.5px] transition-colors duration-150 ${
        checked ? 'border-accent' : 'border-[rgba(26,35,50,0.3)]'
      }`}
    >
      <motion.span
        className="h-2 w-2 rounded-full bg-accent"
        initial={false}
        animate={
          checked
            ? { scale: 1, opacity: 1 }
            : { scale: reduceMotion ? 1 : 0, opacity: 0 }
        }
        transition={{ duration: reduceMotion ? 0 : 0.18, ease: 'easeOut' }}
      />
    </span>
  );
}

/**
 * One choice button, variant-aware. Press-down feedback via whileTap (skipped
 * under prefers-reduced-motion). Spotlight options mount with a small stagger
 * so a fresh question's answers arrive like dealt cards.
 */
export function ChoiceButton({
  active,
  blocked = false,
  variant,
  role,
  index = 0,
  onSelect,
  children,
  leading,
  testId,
}: {
  active: boolean;
  blocked?: boolean;
  variant: FieldVariant;
  role: 'radio' | 'checkbox';
  /** Mount-stagger index (spotlight only). */
  index?: number;
  onSelect: () => void;
  children: React.ReactNode;
  /** Optional leading glyph rendered in-flow (multi-select checkbox). */
  leading?: React.ReactNode;
  /** Stable automation anchor for pinflow + e2e. Callers pass the scoped,
   *  injectively-joined form `option-<fieldKey>-<value>` (via testAnchorJoin)
   *  — a bare `option-<value>` collides across fields on a clustered sheet. */
  testId?: string;
}) {
  const reduceMotion = useReducedMotion() ?? false;
  const spotlight = variant === 'spotlight';
  const classes = spotlight
    ? `${OPTION_BASE} ${active ? OPTION_ACTIVE : blocked ? OPTION_BLOCKED : OPTION_INACTIVE}`
    : `${CHIP_BASE} ${active ? CHIP_ACTIVE : blocked ? CHIP_BLOCKED : CHIP_INACTIVE} ${leading ? 'gap-2' : ''} max-w-full`;
  return (
    <motion.button
      type="button"
      role={role}
      aria-checked={active}
      aria-disabled={blocked || undefined}
      data-testid={testId}
      onClick={onSelect}
      initial={
        spotlight && !reduceMotion ? { opacity: 0, y: 8 } : { opacity: 1, y: 0 }
      }
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: 'easeOut', delay: spotlight ? index * 0.05 : 0 }}
      whileTap={blocked || reduceMotion ? undefined : { scale: spotlight ? 0.985 : 0.97 }}
      className={classes}
    >
      {leading}
      {/* min-w-0 + overflow-wrap:anywhere so a long option label (or an
          unbreakable token like a URL) wraps and lets the flex item shrink,
          instead of forcing the button — and the whole screen — wider than the
          viewport. textContent is unchanged (the clustered parity suite matches
          options by button text). */}
      <span className="min-w-0 [overflow-wrap:anywhere]">{children}</span>
    </motion.button>
  );
}

/**
 * Validation-aware multi-select cue, rendered as a teal eyebrow under the
 * question label so a member reads "this question takes more than one answer"
 * before tapping a single option. Text adapts to the author's selection bounds:
 *  - `min`+`max` equal      → "Select exactly N"
 *  - `min`+`max` distinct   → "Select N to M"
 *  - `max` only (≥2)        → "Select up to M"
 *  - `min` only (≥2)        → "Select at least N"
 *  - neither meaningful     → "Select all that apply"
 *
 * A `min` of 1 is not a meaningful multi-cue (it just means "required"), so it
 * falls through to the canonical phrasing. The range uses the word "to" rather
 * than an en-dash — clearer in plain text and more predictable for screen-reader
 * announcement (per BE review 2026-05-31, P3).
 */
export function multiSelectHint(validation: MultiSelectValidation): string {
  const min =
    typeof validation.min_selections === 'number' ? validation.min_selections : 0;
  const max =
    typeof validation.max_selections === 'number' ? validation.max_selections : 0;
  const hasMin = min >= 2;
  const hasMax = max >= 2;

  if (hasMin && hasMax) {
    if (min === max) return `Select exactly ${min}`;
    if (max > min) return `Select ${min} to ${max}`;
    return `Select at least ${min}`;
  }
  if (hasMax) return `Select up to ${max}`;
  if (hasMin) return `Select at least ${min}`;
  return 'Select all that apply';
}

/**
 * Leading checkbox affordance for multi-select options — the resting visual
 * signal that a question takes more than one answer (single-selects render
 * plain, fill-only). A soft rounded square (radius-sm — on-brand with the logo
 * mark); the check draws in on selection.
 *
 * `aria-hidden`: the parent button already carries role="checkbox" +
 * aria-checked, and the glyph contributes NO text (downstream code matches
 * options by `button.textContent`). On the teal clustered fill the check reads
 * cream; on the spotlight accent-wash it reads teal.
 */
function PillCheckbox({
  checked,
  onAccent,
}: {
  checked: boolean;
  onAccent: boolean;
}) {
  const reduceMotion = useReducedMotion() ?? false;
  return (
    <span
      aria-hidden="true"
      className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[var(--radius-sm)] border-[1.5px] transition-colors duration-150 ${
        checked
          ? onAccent
            ? 'border-[var(--cream)]/60 text-[var(--cream)]'
            : 'border-accent text-accent'
          : 'border-[rgba(26,35,50,0.3)] text-transparent'
      }`}
    >
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-3 w-3"
      >
        <motion.path
          d="M4 10.5L8 14.5L16 6"
          initial={false}
          animate={
            checked
              ? { pathLength: 1, opacity: 1 }
              : { pathLength: reduceMotion ? 1 : 0, opacity: 0 }
          }
          transition={{ duration: reduceMotion ? 0 : 0.18, ease: 'easeOut' }}
        />
      </svg>
    </span>
  );
}

export function SingleSelectInput({
  options,
  value,
  onChange,
  variant,
  segmented = false,
  searchable = false,
  labelledById,
  fieldKey,
}: {
  options: { value: string; label: string }[];
  value: unknown;
  onChange: (next: unknown) => void;
  variant: FieldVariant;
  segmented?: boolean;
  searchable?: boolean;
  labelledById?: string;
  /** Scopes option anchors to `option-<fieldKey>-<value>` so two fields with
   *  the same option values stay document-unique (pinflow resolves testids
   *  with a global querySelector). */
  fieldKey?: string;
}) {
  // Search is an explicit authored field setting. Legacy choices stay visible.
  if (!segmented && searchable) {
    return <SearchableSelectInput options={options} value={value} onChange={onChange} labelledById={labelledById} fieldKey={fieldKey} />;
  }
  // Spotlight: always a full-width stack (the demo's register). Clustered:
  // likert/segmented wraps horizontally, standard select stacks content-width.
  const layout =
    variant === 'spotlight'
      ? 'flex w-full flex-col gap-3'
      : segmented
        ? 'flex flex-wrap gap-2'
        : 'flex flex-col items-start gap-2';
  return (
    <div className={layout} role="radiogroup" aria-labelledby={labelledById}>
      {options.map((opt, i) => (
        <ChoiceButton
          key={opt.value}
          role="radio"
          active={value === opt.value}
          variant={variant}
          index={i}
          testId={fieldKey ? `option-${testAnchorJoin(fieldKey, opt.value)}` : `option-${opt.value}`}
          onSelect={() => onChange(opt.value)}
          leading={
            variant === 'spotlight' ? (
              <SpotlightRadio checked={value === opt.value} />
            ) : undefined
          }
        >
          {opt.label}
        </ChoiceButton>
      ))}
    </div>
  );
}

export function MultiSelectInput({
  options,
  value,
  onChange,
  maxSelections,
  variant,
  labelledById,
  describedById,
  fieldKey,
}: {
  options: FormOption[];
  value: unknown;
  onChange: (next: unknown) => void;
  /** Authored upper bound — once reached, further options are non-selectable. */
  maxSelections?: number | null;
  variant: FieldVariant;
  labelledById?: string;
  /** id of the validation cue ("Select at least 2") — described, not named. */
  describedById?: string;
  /** Scopes option anchors to `option-<fieldKey>-<value>` (see SingleSelectInput). */
  fieldKey?: string;
}) {
  const selected = Array.isArray(value)
    ? (value.filter((v): v is string => typeof v === 'string'))
    : [];
  // Exclusive options ("None of the above"): selecting one clears everything
  // else; selecting anything else clears it. Only the non-exclusive selection
  // count competes with max_selections — an exclusive pick always lands as a
  // single-value answer, so it is never blocked by the bound.
  const exclusiveValues = new Set(
    options.filter(isMutuallyExclusiveMultiSelectOption).map((opt) => opt.value),
  );
  const nonExclusiveSelected = selected.filter((v) => !exclusiveValues.has(v));
  const atMax =
    typeof maxSelections === 'number' &&
    maxSelections >= 1 &&
    nonExclusiveSelected.length >= maxSelections;
  const toggle = (optValue: string) => {
    if (selected.includes(optValue)) {
      onChange(selected.filter((v) => v !== optValue));
      return;
    }
    if (exclusiveValues.has(optValue)) {
      onChange([optValue]);
      return;
    }
    // At the authored max — block further selection so the UI can't let the
    // member violate the bound the cue already states ("Select up to N"). They
    // must deselect one to swap. Backend stays the canonical validator.
    if (atMax) return;
    onChange([...nonExclusiveSelected, optValue]);
  };
  // Multi-select options carry a leading checkbox glyph so the "pick more than
  // one" affordance reads at rest, before any tap — single-select stays plain
  // (fill-only). role="checkbox" + aria-checked on the button carry the
  // semantics for assistive tech; role="group" + aria-labelledby associate the
  // whole set with the question label, and aria-describedby points at the
  // validation cue so AT users get the same bound guidance sighted members read.
  return (
    <div
      className={
        variant === 'spotlight'
          ? 'flex w-full flex-col gap-3'
          : 'flex flex-col items-start gap-2'
      }
      role="group"
      aria-labelledby={labelledById}
      aria-describedby={describedById}
    >
      {options.map((opt, i) => {
        const active = selected.includes(opt.value);
        const blocked = !active && atMax && !exclusiveValues.has(opt.value);
        return (
          <ChoiceButton
            key={opt.value}
            role="checkbox"
            active={active}
            blocked={blocked}
            variant={variant}
            index={i}
            testId={fieldKey ? `option-${testAnchorJoin(fieldKey, opt.value)}` : `option-${opt.value}`}
            onSelect={() => toggle(opt.value)}
            leading={
              <PillCheckbox
                checked={active}
                onAccent={variant === 'clustered' && active}
              />
            }
          >
            <span>{opt.label}</span>
          </ChoiceButton>
        );
      })}
    </div>
  );
}
